import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import StyledButton from "./Button.tsx";
import {
  ModalInput,
  ModalLabel,
  ModalButtonRow,
  CancelButton,
  SmallText,
  ErrorMessage,
} from "./ModalFormComponents.tsx";
import { updateCategoryDocument } from "../services/categories.service.ts";
import { parseApiError } from "../utils/errorHandler.ts";

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  nodeId: number;
  documentId: number;
  initialName: string;
  initialDescription?: string;
  onSuccess?: () => void;
}

export default function EditDocumentModal({
  isOpen,
  onClose,
  categoryId,
  nodeId,
  documentId,
  initialName,
  initialDescription = "",
  onSuccess,
}: EditDocumentModalProps) {
  const [docName, setDocName] = useState("");
  const [docDescription, setDocDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDocName(initialName);
      setDocDescription(initialDescription ?? "");
      setError(null);
    }
  }, [isOpen, initialName, initialDescription]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    if (!docName.trim()) {
      setError("Navn er påkrævet");
      return;
    }

    setSaving(true);
    try {
      await updateCategoryDocument(categoryId, nodeId, documentId, {
        name: docName.trim(),
        description: docDescription.trim(),
      });
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rediger dokument">
      <SmallText>Opdater navn og beskrivelse for dokumentet.</SmallText>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ModalLabel>Navn *</ModalLabel>
      <ModalInput
        placeholder="f.eks. Installationsvejledning"
        value={docName}
        onChange={(e) => setDocName(e.target.value)}
        style={{ marginBottom: "0.75rem" }}
      />

      <ModalLabel>Beskrivelse</ModalLabel>
      <ModalInput
        placeholder="Beskrivelse (valgfrit)"
        value={docDescription}
        onChange={(e) => setDocDescription(e.target.value)}
      />

      <ModalButtonRow>
        <CancelButton onClick={handleClose} disabled={saving}>
          Annuller
        </CancelButton>
        <StyledButton onClick={handleSave} disabled={saving}>
          {saving ? "Gemmer..." : "Gem"}
        </StyledButton>
      </ModalButtonRow>
    </Modal>
  );
}
